import { useState } from 'react';
import { AvisoTope } from '../componentes/AvisoTope';
import { Bloque, Cifra, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { EtiquetaConductor, tonoRealDe } from '../componentes/EtiquetaConductor';
import { haceCuanto, laFecha } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { TOPE_FILAS_CONDUCTORES, obtenerConductores } from '../dominio/conductoresApi';
import { ErrorApi } from '../dominio/clienteApi';
import { reconciliarSuscripcion } from '../dominio/suscripcionApi';
import type { ResultadoReconciliacion } from '../dominio/suscripcionApi';

/**
 * 🔑 **Esta pantalla y `Conductores.tsx` consumen EL MISMO endpoint** (`GET /consola/conductores`)
 * a través del MISMO cliente, `dominio/conductoresApi.ts`. Es a propósito: la fila del listado ya
 * trae `suscripcion.estado`, así que un segundo cliente sólo habría duplicado el contrato para
 * que las dos copias se separaran en cuanto alguien tocara una.
 *
 * 🔴 **Lo que este listado NO trae, y por eso la tabla perdió cuatro columnas del mock.** El
 * servicio proyecta de la suscripción **sólo `estado`**:
 *  - `vigenteHasta`, `hubPagosSubId` y `ultimaSyncAt` **existen en el modelo pero no en el
 *    listado** ⇒ se ven por conductor puntual, y el bloque de reconciliación de abajo es
 *    justamente la vía real para eso (los devuelve en su respuesta).
 *  - **`montoMensual` y `moneda` no existen en ninguna parte**: no es que el endpoint no los
 *    proyecte, es que el modelo `Suscripcion` no tiene esas columnas. El importe del plan vive en
 *    hub-pagos, la fuente de verdad del cobro (R-09). La columna «Monto» era una invención del
 *    mock; dejarla con un guión permanente afirmaría que el dato existe y no llegó.
 *
 * ⚠️ **Y por eso la cifra «Sin sincronizar» del mock ya no está.** Se calculaba con
 * `ultimaSyncAt`, que el listado no devuelve: mostrarla daría el mismo número siempre —
 * exactamente el indicador que no puede fallar y que por eso no informa. Se reemplaza por «Sin
 * suscripción», que sí es computable con lo que llega y además dice algo operativo: sin
 * suscripción `ACTIVA` no se entra al reparto (R-01).
 */

/** El mensaje que ve el operador cuando la reconciliación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/**
 * Estado de la reconciliación manual. **No es `useCargaReal`** porque esto no carga al montar: es
 * una acción bajo demanda, mismo patrón que `ejecutar()` en `Verificacion.tsx` / `Incidentes.tsx`.
 * `conductorId` viaja en el estado para poder decir DE QUIÉN es el resultado que se está viendo:
 * ahora la acción se puede disparar desde cualquier fila, así que sin eso el panel de abajo sería
 * ambiguo apenas se use dos veces seguidas.
 */
type EstadoReconciliacion =
  | { estado: 'inactivo' }
  | { estado: 'cargando'; conductorId: string }
  | { estado: 'listo'; conductorId: string; resultado: ResultadoReconciliacion }
  | { estado: 'error'; conductorId: string; mensaje: string };

export function Suscripciones() {
  // Sin filtro: la pantalla necesita el padrón completo para contar cuántos NO tienen suscripción,
  // y ese es justo el caso que un filtro server-side por estado dejaría afuera.
  const [carga, recargar] = useCargaReal(() => obtenerConductores(), []);
  const ahora = new Date();

  const [conductorId, setConductorId] = useState('');
  const [reconciliacion, setReconciliacion] = useState<EstadoReconciliacion>({
    estado: 'inactivo',
  });

  /**
   * La ÚNICA acción real que esta pantalla tenía antes de conectarse el listado, y sigue
   * funcionando igual: `POST /suscripcion/:conductorId/reconciliar`. Lo único que cambió es que
   * ahora se puede disparar también desde una fila (antes el botón de la tabla estaba
   * deshabilitado porque la tabla era de ejemplo y sus ids no existían en el backend).
   */
  async function reconciliar(id: string): Promise<void> {
    const limpio = id.trim();
    if (!limpio) return;
    setReconciliacion({ estado: 'cargando', conductorId: limpio });
    try {
      const resultado = await reconciliarSuscripcion(limpio);
      setReconciliacion({ estado: 'listo', conductorId: limpio, resultado });
      // La reconciliación puede reescribir `Suscripcion.estado`, que es justo la columna que
      // muestra la tabla: sin esto, la fila seguiría mostrando el estado viejo hasta el próximo
      // montaje y el operador leería como «no pasó nada» algo que sí pasó.
      recargar();
    } catch (error) {
      setReconciliacion({ estado: 'error', conductorId: limpio, mensaje: mensajeDeFalla(error) });
    }
  }

  const listos = carga.estado === 'listo' ? carga.datos : [];
  const enElTope = listos.length >= TOPE_FILAS_CONDUCTORES;

  const filas = listos.filter((c) => c.suscripcion !== null);
  const activas = filas.filter((c) => c.suscripcion?.estado === 'ACTIVA').length;
  const enProblema = filas.filter(
    (c) => c.suscripcion?.estado === 'VENCIDA' || c.suscripcion?.estado === 'FALLIDA',
  ).length;
  const sinSuscripcion = listos.length - filas.length;

  return (
    <Pagina
      rotulo="Conductores"
      titulo="Suscripciones"
      ayuda="El estado del cobro mensual de cada conductor, leído del backend, y la reconciliación manual contra hub-pagos."
    >
      {/*
        hub-pagos es la FUENTE DE VERDAD del cobro (R-09). Lo que se guarda del lado nuestro es un
        puntero y una copia del último estado conocido, no el estado. Por eso la reconciliación no
        es un botón de conveniencia: es la segunda vía —aparte del webhook— para que un webhook
        perdido no deje a un conductor pagado sin servicio (CA-02.2).
      */}
      <div className="cifras">
        <Cifra rotulo="Activas" valor={activas} nota="habilitadas para el reparto" />
        <Cifra
          rotulo="Vencidas o rechazadas"
          valor={enProblema}
          nota="fuera del reparto"
          tono={enProblema > 0 ? 'alarma' : undefined}
        />
        <Cifra
          rotulo="Sin suscripción"
          valor={sinSuscripcion}
          nota="ninguna fila de suscripción"
          tono={sinSuscripcion > 0 ? 'atencion' : undefined}
        />
      </div>

      <div className="bloque">
        <div className="bloque__cabecera">
          <div>
            <h2 className="bloque__titulo">Conciliación con hub-pagos</h2>
            <p className="campo__ayuda" style={{ marginTop: 2 }}>
              El estado real del cobro vive en hub-pagos. Acá se ve el último estado que quedó
              espejado de este lado; «Volver a consultar» lo compara contra la fuente ahora mismo.
            </p>
          </div>
          <span className="tenue">
            {filas.length} con suscripción de {listos.length}
            {enElTope ? ' (tope)' : ''}
          </span>
        </div>

        {carga.estado === 'listo' && enElTope ? (
          <AvisoTope
            tope={TOPE_FILAS_CONDUCTORES}
            sugerencia="Estas cifras cuentan sólo las filas que llegaron: con el tope alcanzado son un piso, no un total."
          />
        ) : null}

        {carga.estado === 'cargando' ? (
          <Cargando filas={4} etiqueta="Cargando suscripciones" />
        ) : carga.estado === 'error' ? (
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        ) : filas.length === 0 ? (
          <Vacio
            titulo="Ningún conductor tiene suscripción todavía"
            detalle={
              listos.length > 0
                ? `Hay ${listos.length} conductor(es) en el padrón, ninguno con fila de suscripción.`
                : 'Tampoco hay conductores en el padrón.'
            }
          />
        ) : (
          <Tabla cabeceras={['Conductor', 'Estado', 'Expediente', 'Disponibilidad', '']}>
            {filas.map((conductor) => {
              const suscripcion = conductor.suscripcion;
              if (!suscripcion) return null;
              const ocupado =
                reconciliacion.estado === 'cargando' && reconciliacion.conductorId === conductor.id;
              return (
                <tr key={conductor.id} data-estado={tonoRealDe('suscripcion', suscripcion.estado)}>
                  <td>
                    {conductor.nombre ?? <span className="apagado">sin nombre</span>}
                    <div className="apagado">{conductor.id}</div>
                  </td>
                  <td>
                    <EtiquetaConductor tipo="suscripcion" valor={suscripcion.estado} />
                  </td>
                  {/*
                    Estas dos columnas no son relleno de las que se perdieron: son las que explican
                    si el conductor está fuera del reparto por el COBRO o por otra cosa. Con sólo
                    el estado de la suscripción, una fila «activa» que igual no recibe viajes no se
                    puede diagnosticar desde acá.
                  */}
                  <td>
                    <EtiquetaConductor tipo="verificacion" valor={conductor.estadoVerificacion} />
                  </td>
                  <td>
                    <EtiquetaConductor
                      tipo="disponibilidad"
                      valor={conductor.estadoDisponibilidad}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="boton boton--chico"
                      disabled={ocupado}
                      onClick={() => {
                        setConductorId(conductor.id);
                        void reconciliar(conductor.id);
                      }}
                    >
                      {ocupado ? 'Consultando…' : 'Volver a consultar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </Tabla>
        )}
      </div>

      {/*
        CA-02.2/CA-02.3 — la reconciliación manual, que ya era real antes de este cambio y sigue
        igual. Se conserva la entrada por id a mano ADEMÁS del botón por fila: un conductor que no
        entró en el tope, o que ni siquiera tiene fila de suscripción todavía, no aparece arriba y
        aun así hay que poder reconciliarlo.
      */}
      <Bloque
        titulo="Reconciliar un conductor contra hub-pagos"
        nota="Dato real: esto llama al backend de verdad. Sirve para cualquier conductor, esté o no en la tabla de arriba."
      >
        <p className="campo__ayuda" style={{ marginTop: 0, marginBottom: 12 }}>
          Un webhook perdido puede dejar a un conductor pagado sin servicio: esta es la segunda vía,
          aparte del webhook, para que eso no quede así. Pedile al conductor su id (o buscalo en
          Verificación) y disparala a mano.
        </p>
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="campo" style={{ flex: '1 1 320px' }}>
            <span className="campo__etiqueta rotulo">Id del conductor</span>
            <input
              className="entrada"
              type="text"
              placeholder="uuid del conductor"
              value={conductorId}
              onChange={(evento) => setConductorId(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter') void reconciliar(conductorId);
              }}
            />
          </label>
          <button
            type="button"
            className="boton boton--principal"
            style={{ alignSelf: 'flex-end' }}
            disabled={conductorId.trim() === '' || reconciliacion.estado === 'cargando'}
            onClick={() => void reconciliar(conductorId)}
          >
            {reconciliacion.estado === 'cargando' ? 'Reconciliando…' : 'Reconciliar'}
          </button>
        </div>

        {reconciliacion.estado === 'error' ? (
          <div style={{ marginTop: 12 }}>
            <ConError
              titulo={`No se pudo reconciliar ${reconciliacion.conductorId}`}
              detalle={reconciliacion.mensaje}
              alReintentar={() => void reconciliar(reconciliacion.conductorId)}
            />
          </div>
        ) : reconciliacion.estado === 'listo' ? (
          <div style={{ marginTop: 12 }}>
            <p className="campo__ayuda" style={{ marginTop: 0 }}>
              Resultado para <strong>{reconciliacion.conductorId}</strong>:
            </p>
            {!reconciliacion.resultado.reconciliado || !reconciliacion.resultado.suscripcion ? (
              <p className="apagado">
                hub-pagos no tiene ninguna suscripción para este conductor. No es un error: es la
                respuesta cuando no hay nada que sincronizar.
                {/*
                  ⚠️ En este ambiente de desarrollo esto es lo único que se puede ver: el
                  `CLIENTE_HUB_PAGOS` de `suscripcion.module.ts` es el stub de desarrollo y
                  siempre devuelve null (verificado por efecto — ver `dominio/suscripcionApi.ts`).
                */}
              </p>
            ) : (
              /*
                🔑 Estos cuatro campos son los que el LISTADO no trae. La reconciliación sí los
                devuelve, así que la única vía para verlos hoy es esta — y por eso el bloque no es
                un accesorio de la tabla: es la mitad que falta.
              */
              <dl className="datos">
                <dt style={{ color: '#6a6154' }}>Estado</dt>
                <dd>
                  <EtiquetaConductor
                    tipo="suscripcion"
                    valor={reconciliacion.resultado.suscripcion.estado}
                  />
                </dd>
                <dt style={{ color: '#6a6154' }}>Vigente hasta</dt>
                <dd>{laFecha(reconciliacion.resultado.suscripcion.vigenteHasta)}</dd>
                <dt style={{ color: '#6a6154' }}>Referencia en hub-pagos</dt>
                <dd>{reconciliacion.resultado.suscripcion.hubPagosSubId ?? '—'}</dd>
                <dt style={{ color: '#6a6154' }}>Sincronizado</dt>
                <dd>{haceCuanto(reconciliacion.resultado.suscripcion.ultimaSyncAt, ahora)}</dd>
              </dl>
            )}
          </div>
        ) : null}
      </Bloque>

      <Bloque titulo="Qué se puede y qué no desde acá">
        <p className="pagina__ayuda" style={{ marginTop: 0 }}>
          Esta pantalla <strong>lee</strong> el estado del cobro y lo vuelve a comparar contra
          hub-pagos; no cobra, no reintenta y no da de baja un plan. Todo eso vive en hub-pagos. Si
          una suscripción está vencida y el conductor dice que pagó, lo que corresponde es volver a
          consultar y, si sigue sin cuadrar, revisarlo en hub-pagos — no reactivar la cuenta a mano
          acá. Una excepción de cortesía se modela como una suscripción activa con otro origen, que
          queda visible y con fecha; nunca como un permiso suelto.
        </p>
        <p className="pagina__ayuda">
          ⚠️ La tabla muestra <strong>sólo el estado</strong> de cada suscripción: el listado admin
          no devuelve la vigencia, la referencia en hub-pagos ni la última sincronización, y el
          importe del plan no existe de este lado (vive en hub-pagos). Esos cuatro datos aparecen en
          el resultado de la reconciliación, conductor por conductor.
        </p>
      </Bloque>
    </Pagina>
  );
}
